import { useState } from 'react'
import { getErrorMessage } from '@/types/firebase'

interface Props {
  onClose: () => void
}

const CATEGORIES = [
  { value: 'google',          label: 'Google sign-in' },
  { value: 'facebook',        label: 'Facebook sign-in' },
  { value: 'fingerprint-pin', label: 'Fingerprint / PIN' },
  { value: '2fa',             label: '2FA' },
  { value: 'other',           label: 'Other' },
]

export function SignInHelpModal({ onClose }: Props) {
  const [name, setName]           = useState('')
  const [email, setEmail]         = useState('')
  const [message, setMessage]     = useState('')
  const [category, setCategory]   = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError]         = useState<string | null>(null)

  async function handleSubmit() {
    if (!name.trim() || !email.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_KEY ?? '',
          subject: `[David's Beacon] Sign-in issue${category ? ` (${category})` : ''} — ${name.trim()}`,
          name: name.trim(),
          email: email.trim(),
          category: category || 'Not specified',
          message: message.trim() || '(No details provided)',
          botcheck: '',
        }),
      })
      const data = await res.json() as { success: boolean }
      if (data.success) {
        setSubmitted(true)
      } else {
        setError("Could not send. Email us at hello@davidsbeacon.ph")
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Network error. Email us at hello@davidsbeacon.ph'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <i className="fa-solid fa-xmark text-lg" />
        </button>

        {submitted ? (
          <div className="text-center py-4 space-y-3">
            <div className="w-12 h-12 rounded-full bg-beacon-100 flex items-center justify-center mx-auto">
              <i className="fa-solid fa-check text-beacon-600 text-lg" />
            </div>
            <p className="font-semibold text-slate-900">Message sent!</p>
            <p className="text-sm text-slate-500">We'll get back to you at the email you provided.</p>
            <button onClick={onClose} className="text-sm font-medium text-beacon-500 hover:text-beacon-700 transition-colors">
              Close
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="font-bold text-slate-900">Having trouble signing in?</h3>
            <p className="text-sm text-slate-500">Let us know and we'll help sort it out.</p>

            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1.5">What's the issue?</p>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategory((c) => c === cat.value ? '' : cat.value)}
                    className={[
                      'px-3 py-1 rounded-full border text-xs font-medium transition-colors',
                      category === cat.value
                        ? 'bg-beacon-600 text-white border-beacon-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-beacon-400 hover:text-beacon-600',
                    ].join(' ')}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-beacon-400"
            />
            <input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-beacon-400"
            />
            <textarea
              rows={3}
              placeholder="What's happening? (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-beacon-400 resize-none"
            />

            {error && <p className="text-xs text-danger-500">{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={!name.trim() || !email.trim() || submitting}
              className="w-full bg-beacon-600 text-white font-semibold rounded-xl px-4 py-2.5 text-sm hover:bg-beacon-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending…' : 'Send message'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
